import { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Control, FormState } from 'react-hook-form';
import { Typography } from '@mui/material';
import * as _ from 'lodash';

import { LegalEntityStatus } from '../../data/models/legal-entity-status.model';
import { LegalEntityType } from '../../data/models/legal-entity-type.model';
import OnboardForm, { formClasses } from './OnboardForm';
import TextInput2 from '../form/TextInput2';
import SelectMenu2 from '../form/SelectMenu2';
import { TransportationType } from '../../data/models/transportation-type.model';
import CheckboxInput2 from '../form/CheckboxInput2';
import { StateCode } from '../../data/models/state-code.model';
import validationUtils from '../../services/validationUtils';

interface AddressProps {
  namePrefix: string;
  control: Control;
  formState: FormState<any>;
  requiredFieldMessage: string;
  stateCodes: StateCode[];
  lineOneLabel?: string;
}

export function Address({
  namePrefix,
  control,
  formState,
  requiredFieldMessage,
  stateCodes,
  lineOneLabel,
}: AddressProps) {
  const { t } = useTranslation('common');

  return (
    <>
      <TextInput2
        label={lineOneLabel || t('common.address1')}
        name={namePrefix + 'AddressLine1'}
        className={formClasses.control}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
        inputProps={{ maxLength: 200 }}
        required
      />
      <TextInput2
        label={t('common.address2')}
        className={formClasses.control}
        name={namePrefix + 'AddressLine2'}
        required={false}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 200 }}
      />
      <TextInput2
        label={t('common.city')}
        name={namePrefix + 'City'}
        className={formClasses.control}
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
        inputProps={{ maxLength: 100 }}
      />
      <SelectMenu2
        label={t('common.state')}
        name={namePrefix + 'State'}
        className={formClasses.control}
        values={stateCodes}
        valueAttr="stateCode"
        labelAttr="stateName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      <TextInput2
        label={t('common.zip')}
        className={formClasses.control}
        name={namePrefix + 'ZipCode'}
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: requiredFieldMessage,
          pattern: validationUtils.zipCodeValidation(),
        }}
        inputProps={{ maxLength: 5 }}
      />
    </>
  );
}

interface BusinessFormProps {
  legalEntityTypes: LegalEntityType[];
  legalEntityStatuses: LegalEntityStatus[];
  providerTypes: TransportationType[];
  stateCodes: StateCode[];
  defaultValues: any;
  onSubmit: (values: any) => void;
}

const BusinessForm = forwardRef(
  (
    {
      legalEntityTypes,
      legalEntityStatuses,
      providerTypes,
      stateCodes,
      defaultValues,
      onSubmit,
    }: BusinessFormProps,
    ref,
  ) => {
    const { t } = useTranslation('common');
    const requiredFieldMessage = t('common.requiredField');
    const { control, formState, handleSubmit, getValues, watch, setValue } =
      useForm({
        defaultValues: _.pick(defaultValues, [
          'TransportationProviderName',
          'LegalEntityTypeID',
          'LegalEntityStatusID',
          'AddressLine1',
          'AddressLine2',
          'City',
          'State',
          'ZipCode',
          'PhysicalAddressLine1',
          'PhysicalAddressLine2',
          'PhysicalCity',
          'PhysicalState',
          'PhysicalZipCode',
          'BillingAddressLine1',
          'BillingAddressLine2',
          'BillingCity',
          'BillingState',
          'BillingZipCode',
          'TransportationTypeID',
          'NPINumber',
          'HasUnlimitedTrips',
          'BillingSameAsOffice',
          'DispatchSameAsOffice',
        ]),
      });

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit(onSubmit),
      getValues,
    }));

    const billingSame = watch('BillingSameAsOffice');
    const dispatchSame = watch('DispatchSameAsOffice');

    useEffect(() => {
      if (billingSame) {
        setValue('BillingAddressLine1', watch('PhysicalAddressLine1'));
        setValue('BillingAddressLine2', watch('PhysicalAddressLine2'));
        setValue('BillingCity', watch('PhysicalCity'));
        setValue('BillingState', watch('PhysicalState'));
        setValue('BillingZipCode', watch('PhysicalZipCode'));
      }
    }, [billingSame]);

    useEffect(() => {
      if (dispatchSame) {
        setValue('AddressLine1', watch('PhysicalAddressLine1'));
        setValue('AddressLine2', watch('PhysicalAddressLine2'));
        setValue('City', watch('PhysicalCity'));
        setValue('State', watch('PhysicalState'));
        setValue('ZipCode', watch('PhysicalZipCode'));
      }
    }, [dispatchSame]);

    return (
      <OnboardForm title={t('onboard.businessInfo')}>
        <TextInput2
          label={t('providers.provider')}
          name={'TransportationProviderName'}
          className={formClasses.control}
          control={control}
          errors={formState.errors}
          rules={{ required: requiredFieldMessage }}
          inputProps={{ maxLength: 100 }}
          required
        />
        <SelectMenu2
          label={t('provider.legalEntityType')}
          name="LegalEntityTypeID"
          className={formClasses.control}
          values={legalEntityTypes}
          valueAttr="legalEntityTypeID"
          labelAttr="legalEntityTypeName"
          required={true}
          rules={{ required: requiredFieldMessage }}
          control={control}
          errors={formState.errors}
        />
        <SelectMenu2
          label={t('provider.legalEntityStatus')}
          name="LegalEntityStatusID"
          className={formClasses.control}
          values={legalEntityStatuses}
          valueAttr="legalEntityStatusID"
          labelAttr="legalEntityStatusName"
          required={true}
          rules={{ required: requiredFieldMessage }}
          control={control}
          errors={formState.errors}
        />
        <Address
          namePrefix="Physical"
          control={control}
          formState={formState}
          stateCodes={stateCodes}
          lineOneLabel={t('provider.physicalAddressLine1') || ''}
          requiredFieldMessage={requiredFieldMessage}
        />
        <Typography className={formClasses.subtitle}>
          {t('provider.billingAddress')}
        </Typography>
        <CheckboxInput2
          label={t('onboard.sameAsOwner')}
          name="BillingSameAsOffice"
          className={formClasses.checkbox}
          required={false}
          control={control}
        />
        {!billingSame && (
          <Address
            namePrefix="Billing"
            control={control}
            formState={formState}
            stateCodes={stateCodes}
            lineOneLabel={t('provider.billingAddressLine1') || ''}
            requiredFieldMessage={requiredFieldMessage}
          />
        )}
        <Typography className={formClasses.subtitle}>
          {t('provider.dispatchAddress')}
        </Typography>
        <CheckboxInput2
          label={t('onboard.sameAsOwner')}
          name="DispatchSameAsOffice"
          className={formClasses.checkbox}
          required={false}
          control={control}
        />
        {!dispatchSame && (
          <Address
            namePrefix=""
            control={control}
            formState={formState}
            stateCodes={stateCodes}
            lineOneLabel={t('provider.dispatchAddressLine1') || ''}
            requiredFieldMessage={requiredFieldMessage}
          />
        )}
        <SelectMenu2
          label={t('provider.type')}
          name="TransportationTypeID"
          className={formClasses.control}
          values={providerTypes}
          valueAttr="transportationTypeID"
          labelAttr="transportationTypeName"
          required={true}
          control={control}
          errors={formState.errors}
          rules={{ required: requiredFieldMessage }}
        />
        <TextInput2
          label={t('provider.npiNumber')}
          name="NPINumber"
          className={formClasses.control}
          required={false}
          control={control}
          errors={formState.errors}
          inputProps={{ maxLength: 10 }}
          rules={{
            pattern: validationUtils.accountNumberValidation(
              t,
              'common.invalidNPINumber',
            ),
          }}
        />
        <CheckboxInput2
          label={t('provider.hasUnlimitedTrips')}
          name="HasUnlimitedTrips"
          className={formClasses.checkbox}
          required={false}
          control={control}
        />
      </OnboardForm>
    );
  },
);

export default BusinessForm;
